import * as yup from "yup";
import * as styles from "./SectionContactForm.module.scss";

import { navigate } from "gatsby";
import React from "react";
import { useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import {
	Button,
	Form,
	FormSelect,
	FormTextArea,
	FormTextInput,
} from "../../../components/common";
import type { SelectedDropdownItem } from "../../../components/common/FormSelect/FormSelect";
import { Colours } from "../../../utils/colours";
import { handleTrailingSlash } from "../../../utils/string";
import WrappedSection from "../../WrappedSection/WrappedSection";

interface ContactUsFormFields {
	recaptchaToken: string;
	contactFormFirstName: string;
	contactFormLastName: string;
	contactFormPhone: string;
	contactFormEmail: string;
	contactFormSubject: SelectedDropdownItem;
	contactFormMessage: string;
}

const subjectOptions =
	process.env.GATSBY_IS_IE !== "true" // IS UK
		? [
				{
					label: "Your details",
					value: "Your details",
				},
				{ label: "Rehoming", value: "Rehoming" },
				{ label: "Fostering", value: "Fostering" },
				{ label: "Donations", value: "Donations" },
				{ label: "Sponsorship", value: "Sponsorship" },
				{ label: "Membership", value: "Membership" },
				{ label: "Fundraising", value: "Fundraising" },
				{ label: "Volunteering", value: "Volunteering" }, // This is unique to the UK
				{ label: "Our services", value: "Our services" },
				{ label: "Compliments & feedback", value: "Compliments & feedback" },
				{ label: "Something else", value: "Something else" },
			]
		: [
				{
					label: "Your details",
					value: "Your details",
				},
				{ label: "Rehoming", value: "Rehoming" },
				{ label: "Fostering", value: "Fostering" },
				{ label: "Donations", value: "Donations" },
				{ label: "Sponsorship", value: "Sponsorship" },
				{ label: "Membership", value: "Membership" },
				{ label: "Fundraising", value: "Fundraising" },
				{ label: "Our services", value: "Our services" },
				{ label: "Compliments & feedback", value: "Compliments & feedback" },
				{ label: "Something else", value: "Something else" },
			];

export interface SectionContactFormProps {
	heading: string;
	htmlIntroText?: string;
	jumpLinksTitle?: string;
}

export const getSectionContactFormProps = ({
	field_front_end_title,
	field_section_short_description,
	field_jump_links_title,
}: Queries.SectionDataFragment): SectionContactFormProps => ({
	heading: field_front_end_title,
	htmlIntroText: field_section_short_description?.value,
	jumpLinksTitle: field_jump_links_title,
});

export const SectionContactForm: React.FC<SectionContactFormProps> = ({
	heading,
	htmlIntroText,
	jumpLinksTitle,
}) => {
	const { executeRecaptcha } = useGoogleReCaptcha();
	const [isLoading, setIsLoading] = useState(false);
	const [isSubmitted, setIsSubmitted] = useState(false);

	const submitHandler = async (formData: ContactUsFormFields) => {
		setIsLoading(true);
		setIsSubmitted(true);

		const recaptchaToken = await executeRecaptcha?.("contact_form");
		await fetch(process.env.GATSBY_CONTACT_API_URL, {
			method: "POST",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				...formData,
				recaptchaToken,
				contactFormSubject: formData.contactFormSubject.value,
			}),
		})
			.then((res) => {
				setIsLoading(false);
				navigate(
					handleTrailingSlash(
						`${window.location.href}/${res.ok ? "thank-you" : "error"}`,
					),
				);
			})
			.catch((_err) => {
				setIsLoading(false);
				navigate(handleTrailingSlash(`${window.location.href}/${"error"}`));
			});
	};

	return (
		<WrappedSection
			sectionName="SectionContactForm"
			sectionColour={Colours.SANDY_FUR}
		>
			<div className={styles.innerwrapper}>
				<div className={styles.titlewrapper}>
					{jumpLinksTitle ? (
						<h2 className={styles.title} data-jumplinkstitle={jumpLinksTitle}>
							{heading}
						</h2>
					) : (
						<h2 className={styles.title}>{heading}</h2>
					)}
				</div>
				<div
					className={styles.introtext}
					// biome-ignore lint/security/noDangerouslySetInnerHtml: Safe source
					dangerouslySetInnerHTML={{
						__html: htmlIntroText,
					}}
				/>
				<div className={styles.formcontainer}>
					<Form
						className={styles.contactform}
						onSubmitFn={submitHandler}
						validationSchema={{
							contactFormFirstName: yup
								.string()
								.required("Please enter your first name")
								.matches(
									/^[A-zÀ-ÿ\-\u0621-\u064A\u0401\u0451\u0410-\u044f\' ]*$/,
									"Must only be characters A-Z and international characters",
								)
								.min(1, "Please enter your first name")
								.max(40, "Please enter a first name less than 40 characters"),
							contactFormLastName: yup
								.string()
								.required("Please enter your last name")
								.matches(
									/^[A-zÀ-ÿ\-\u0621-\u064A\u0401\u0451\u0410-\u044f\' ]*$/,
									"Must only be characters A-Z and international characters",
								)
								.min(1, "Please enter your last name")
								.max(40, "Please enter a last name less than 40 characters"),
							contactFormPhone: yup
								.string()
								.required("Please enter your phone number")
								.matches(/^[0-9\s]*$/, "Must be only digits")
								.min(11, "Must be a minimum of 11 digits"),
							contactFormEmail: yup
								.string()
								.trim()
								.email("That doesn't look like a valid email")
								.required("Please enter your email address"),
							contactFormSubject: yup
								.object()
								.required("Please choose a subject for the message"),
							contactFormMessage: yup
								.string()
								.required("Please enter your message")
								.min(10, "Your message must be at least 10 characters"),
						}}
					>
						<FormTextInput
							className={styles.input}
							placeholder="First name"
							name="contactFormFirstName"
							label="First name"
							autoComplete="given-name"
						/>
						<FormTextInput
							className={styles.input}
							placeholder="Last name"
							name="contactFormLastName"
							label="Last name"
							autoComplete="family-name"
						/>
						<FormTextInput
							className={styles.input}
							placeholder="Phone number"
							name="contactFormPhone"
							maxLength={20}
							label="Phone number"
							autoComplete="tel"
						/>
						<FormTextInput
							className={styles.input}
							placeholder="Email"
							name="contactFormEmail"
							errorMessage="That doesn't look like a valid email"
							type="email"
							label="Email"
							autoComplete="email"
						/>
						<label
							htmlFor="contactFormSubject"
							className={styles.textinputlabel}
						>
							Subject
						</label>
						<FormSelect
							className={styles.select}
							name="contactFormSubject"
							label=""
							placeholder="Subject"
							options={subjectOptions}
						/>
						<FormTextArea
							className={styles.inputtextarea}
							name="contactFormMessage"
							placeholder="Your message"
							label="Your message"
						/>
						<Button
							variant="primary"
							type="submit"
							ariaLabel="submit"
							fullWidth={true}
							onClick={() => {}}
							isLoading={isLoading}
							disabled={isSubmitted}
						>
							Send Message
						</Button>
					</Form>
				</div>
			</div>
		</WrappedSection>
	);
};

export default SectionContactForm;
